/**
 * Steps Component
 * Composant purement CSS, pas de JavaScript nécessaire pour le fonctionnement de base
 * Ce fichier contient des fonctions utilitaires optionnelles
 */

/**
 * Initialise les steps
 * Pas d'initialisation nécessaire car le composant est purement CSS
 * Cette fonction est conservée pour la compatibilité avec l'architecture
 */
export function initSteps() {
  // API globale : utilisable sans import de module (bundle IIFE, npm)
  if (typeof window !== 'undefined') {
    window.Steps = { setActiveStep, completeStep, resetSteps, nextStep, previousStep, getActiveStepIndex };
  }
}

/**
 * Crée l'icône SVG Lucide pour les étapes complétées
 * @returns {HTMLElement} L'élément span contenant l'icône
 */
function createCheckIcon() {
  const span = document.createElement('span');
  span.className = 'step__icon';
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M20 6 9 17l-5-5"/>
  </svg>`;
  return span;
}

/**
 * Marque une étape comme active
 * @param {HTMLElement|string} stepsElement - L'élément .steps ou un sélecteur
 * @param {number} stepIndex - L'index de l'étape (commence à 0)
 */
export function setActiveStep(stepsElement, stepIndex) {
  const element = typeof stepsElement === 'string'
    ? document.querySelector(stepsElement)
    : stepsElement;

  if (!element) return;

  const steps = element.querySelectorAll('.step');

  steps.forEach((step, index) => {
    // Retirer toutes les classes d'état
    step.classList.remove('step--active', 'step--completed');
    step.removeAttribute('aria-current');

    // Retirer l'icône existante si présente
    const existingIcon = step.querySelector('.step__icon');
    if (existingIcon) {
      existingIcon.remove();
    }

    // Marquer comme complété si avant l'étape active
    if (index < stepIndex) {
      step.classList.add('step--completed');
      // Ajouter l'icône au début de l'étape
      step.insertBefore(createCheckIcon(), step.firstChild);
    }
    // Marquer comme active si c'est l'étape courante
    else if (index === stepIndex) {
      step.classList.add('step--active');
      step.setAttribute('aria-current', 'step');
    }
  });
}

/**
 * Marque une étape comme complétée
 * @param {HTMLElement|string} stepsElement - L'élément .steps ou un sélecteur
 * @param {number} stepIndex - L'index de l'étape (commence à 0)
 */
export function completeStep(stepsElement, stepIndex) {
  const element = typeof stepsElement === 'string'
    ? document.querySelector(stepsElement)
    : stepsElement;

  if (!element) return;

  const steps = element.querySelectorAll('.step');
  const step = steps[stepIndex];

  if (step) {
    step.classList.remove('step--active');
    step.removeAttribute('aria-current');
    step.classList.add('step--completed');

    // Ajouter l'icône si elle n'existe pas déjà
    if (!step.querySelector('.step__icon')) {
      step.insertBefore(createCheckIcon(), step.firstChild);
    }
  }
}

/**
 * Réinitialise toutes les étapes
 * @param {HTMLElement|string} stepsElement - L'élément .steps ou un sélecteur
 */
export function resetSteps(stepsElement) {
  const element = typeof stepsElement === 'string'
    ? document.querySelector(stepsElement)
    : stepsElement;

  if (!element) return;

  const steps = element.querySelectorAll('.step');

  steps.forEach((step) => {
    step.classList.remove('step--active', 'step--completed');
    step.removeAttribute('aria-current');

    // Retirer l'icône si présente
    const icon = step.querySelector('.step__icon');
    if (icon) {
      icon.remove();
    }
  });
}

/**
 * Passe à l'étape suivante
 * @param {HTMLElement|string} stepsElement - L'élément .steps ou un sélecteur
 * @returns {number|null} L'index de la nouvelle étape active ou null si déjà à la fin
 */
export function nextStep(stepsElement) {
  const element = typeof stepsElement === 'string'
    ? document.querySelector(stepsElement)
    : stepsElement;

  if (!element) return null;

  const steps = element.querySelectorAll('.step');
  const currentActiveIndex = Array.from(steps).findIndex(step =>
    step.classList.contains('step--active')
  );

  const nextIndex = currentActiveIndex + 1;

  if (nextIndex < steps.length) {
    setActiveStep(element, nextIndex);
    return nextIndex;
  }

  return null;
}

/**
 * Revient à l'étape précédente
 * @param {HTMLElement|string} stepsElement - L'élément .steps ou un sélecteur
 * @returns {number|null} L'index de la nouvelle étape active ou null si déjà au début
 */
export function previousStep(stepsElement) {
  const element = typeof stepsElement === 'string'
    ? document.querySelector(stepsElement)
    : stepsElement;

  if (!element) return null;

  const steps = element.querySelectorAll('.step');
  const currentActiveIndex = Array.from(steps).findIndex(step =>
    step.classList.contains('step--active')
  );

  const prevIndex = currentActiveIndex - 1;

  if (prevIndex >= 0) {
    setActiveStep(element, prevIndex);
    return prevIndex;
  }

  return null;
}

/**
 * Obtient l'index de l'étape active
 * @param {HTMLElement|string} stepsElement - L'élément .steps ou un sélecteur
 * @returns {number} L'index de l'étape active (-1 si aucune)
 */
export function getActiveStepIndex(stepsElement) {
  const element = typeof stepsElement === 'string'
    ? document.querySelector(stepsElement)
    : stepsElement;

  if (!element) return -1;

  const steps = element.querySelectorAll('.step');
  return Array.from(steps).findIndex(step =>
    step.classList.contains('step--active')
  );
}
